import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import { useBranding } from '../../../contexts/BrandingContext';
import useRouteTitle from '../../../hooks/useRouteTitle';

/**
 * BaseSidebarLayout - Reusable base layout with sidebar functionality
 *
 * This component provides the complete layout structure with:
 * - Header (shared AppHeader)
 * - Desktop sidebar (collapsible, persisted to localStorage)
 * - Mobile sidebar (overlay)
 * - Main content area (properly offset for sidebar)
 * - Footer (shared AppFooter)
 * - Dynamic brand name/logo from BrandingContext
 *
 * @param {React.Component} SidebarContent - Component to render as sidebar navigation links
 * @param {string} storageKey - localStorage key for sidebar state persistence (default: 'sidebarOpen')
 * @param {React.ReactNode} children - Optional children to wrap around Outlet
 * @param {React.ReactElement|null} topBanner - Optional above-nav banner (e.g. missing alias)
 *
 * Architecture:
 * - Self-contained: manages its own sidebar state
 * - No external state dependencies
 * - Each role layout passes its own SidebarContent component
 * - Brand name/logo read from BrandingContext (no logoText prop needed)
 */
const BaseSidebarLayout = ({
  SidebarContent,
  storageKey = 'sidebarOpen',
  children,
  topBanner = null
}) => {
  const { branding } = useBranding();
  const platformName = branding.identity.platformName;
  const logoUrl = branding.assets.logoUrl;

  // Per-page document title based on current route (see hooks/useRouteTitle.js)
  useRouteTitle();

  // Sidebar state (persisted to localStorage)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored !== null ? JSON.parse(stored) : true;
  });

  // Mobile sidebar state (not persisted)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [bannerOffset, setBannerOffset] = useState(0);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen, storageKey]);

  // Close mobile sidebar
  const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

  // Toggle mobile sidebar (called from header hamburger)
  const toggleMobileSidebar = () => setIsMobileSidebarOpen(prev => !prev);

  // Sidebar widths
  const sidebarWidthOpen = 200;
  const sidebarWidthClosed = 72;
  const currentSidebarWidth = isSidebarOpen ? sidebarWidthOpen : sidebarWidthClosed;
  const handleBannerVisibility = useCallback((visible) => {
    setBannerOffset(visible ? 40 : 0);
  }, []);

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden">
      {topBanner && (
        <div className="fixed top-0 left-0 right-0 z-40">
          {React.cloneElement(topBanner, { onVisibilityChange: handleBannerVisibility })}
        </div>
      )}
      {/* Header with hamburger connected to mobile sidebar */}
      <AppHeader
        onMobileMenuClick={toggleMobileSidebar}
        sidebarWidth={currentSidebarWidth}
        topOffset={bannerOffset}
      />

      {/* Mobile sidebar overlay */}
      <div
        className={`lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          isMobileSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileSidebar}
      >
        <div
          className={`bg-gray-900 w-[200px] max-w-[80vw] h-full transform transition-transform duration-300 ${
            isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile sidebar header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            {logoUrl ? (
              <img src={logoUrl} alt={platformName} className="h-8 w-auto" />
            ) : (
              <div className="text-brand font-bold text-xl">{platformName}</div>
            )}
            <button
              onClick={closeMobileSidebar}
              className="text-gray-400 hover:text-white"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile sidebar navigation */}
          <nav className="flex-1 px-2 py-4">
            <div className="space-y-1">
              {SidebarContent && (
                <SidebarContent
                  isSidebarOpen={true}
                  onLinkClick={closeMobileSidebar}
                  isMobile={true}
                />
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar - starts below header (h-16 = 4rem = 64px) */}
      <div
        className={`hidden lg:flex bg-gray-900 text-white transition-all duration-300 ease-in-out fixed left-0 z-20 flex-col justify-between ${
          isSidebarOpen ? 'w-[200px]' : 'w-[72px]'
        }`}
        style={{
          top: 64 + bannerOffset,
          height: `calc(100vh - ${64 + bannerOffset}px)`
        }}
      >
        <div>
          {/* Desktop sidebar navigation */}
          <nav className="mt-4">
            <div className="px-3 space-y-1">
              {SidebarContent && (
                <SidebarContent
                  isSidebarOpen={isSidebarOpen}
                  onLinkClick={() => {}}
                  isMobile={false}
                />
              )}
            </div>
          </nav>
        </div>

        {/* Collapse/expand button */}
        <div className="p-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-800 transition-colors"
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isSidebarOpen ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main content wrapper - offset for sidebar and header */}
      <div
        className={`flex-1 flex flex-col transition-[margin] duration-300 ease-in-out ${
          isSidebarOpen ? 'lg:ml-[200px]' : 'lg:ml-[72px]'
        }`}
      >
        {/* Main content area */}
        <main
          className="flex-1 p-4 md:p-6 lg:p-8"
          style={{ marginTop: 64 + bannerOffset }}
        >
          {children || <Outlet />}
        </main>

        {/* Footer */}
        <AppFooter />
      </div>
    </div>
  );
};

export default BaseSidebarLayout;
